import { Component, OnInit, ElementRef, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, debounce, filter, Subscription, timer } from 'rxjs';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';

import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';

import { getMonitoringDateQueryInputTopicOptions } from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';

import { FilterWidgetComponent } from '../../../../Common/Filter/components/FilterWidget/filter-widget.component';
import { SocialSeriesService } from '../../services/social-series.service';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { FilterTypeModel } from '../../../../Common/Filter/models/abstract-filter-type.model';
import type { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ThemeService } from '../../services/theme.service';
import { AgentService } from '../../services/agent.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SocialRoutingService } from '../../../Core/services/social-routing.service';
import { SocialArchiveSearchService } from '../../services/social-archive-search.service';
import { InvalidFiltersError } from '../../../../Monitor/Shared/errors/invalid-filters.error';

enum ThemeAnalyticsTabEnum {
    summary,
    sentiment,
    engagements,
    trends
}

enum SearchErrorTypeEnum {
    none,
    unknown,
    filters,
    queryLength,
    apiLimit
}

@Component({
    selector: 'agpr-theme-analytics',
    templateUrl: './theme-analytics.component.html',
    styleUrls: ['./theme-analytics.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: SocialSeriesService
        }
    ],
    standalone: false
})
export class ThemeAnalyticsComponent implements OnInit, OnDestroy {
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly themeAnalyticsTabEnum = ThemeAnalyticsTabEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly errorTypes = SearchErrorTypeEnum;
    readonly id: string = 'ThemeAnalytics';

    public overlayContainer: Element;
    public dateOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();
    public searchDateRange: DateQueryModel;
    public searchRange: SearchRangeModel = null;
    public currentTabIndex: number = ThemeAnalyticsTabEnum.summary;
    isArchiveSearch: boolean = false;
    enableEngagementInsights: boolean = true;
    themeName: string;

    private subscriptions: Subscription[] = [];
    public activeFilters: FilterTypeModel[];
    public agentIds: number[];
    public filterError: InvalidFiltersError;
    public errorType: SearchErrorTypeEnum = SearchErrorTypeEnum.none;
    constructor(
        private ref: ElementRef,
        private coreDialogService: CoreDialogService,
        private socialSeriesService: SocialSeriesService,
        public monitorConfigService: MonitorConfigService,
        private themeService: ThemeService,
        private agentService: AgentService,
        private socialRoutingService: SocialRoutingService,
        private socialArchiveSearchService: SocialArchiveSearchService
    ) { }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.socialRoutingService.isArchiveSearch$.subscribe(isArchiveSearch => {
                this.isArchiveSearch = isArchiveSearch;
                if (this.isArchiveSearch) {
                    this.socialSeriesService.doSearch(true);
                }
            }),
            this.socialSeriesService.activeSearchFilters$.subscribe(activeFilters => {
                if (activeFilters) {
                    this.activeFilters = activeFilters;
                }
            }),
            this.socialSeriesService.agentIds$.pipe(filter(x => !!x)).subscribe(agentIds => {
                this.agentIds = agentIds;
            }),
            this.monitorConfigService.getSearchRange().subscribe(searchRange => this.searchRange = searchRange),
            this.socialSeriesService.getDateQuery().subscribe(dateQuery => this.searchDateRange = dateQuery),
            this.socialArchiveSearchService.enableEngagementInsights$.subscribe(x => this.enableEngagementInsights = x),
            this.themeService.currentResource$.subscribe(theme => {
                this.themeName = theme?.name || null;
            }),

            // Combine latest is here because some data mappers rely on mediaSubscriptions being loaded to join on the names
            // Specifically `previousPeriodShareOfVoiceBySocialTopicVolumeSeries.mentions.dataMapper`
            // @TODO: NEP-10914 make the data mappers return an observable instead so this loading can be made cleaner
            combineLatest(
                [
                    this.themeService.currentResource$.pipe(filter(x => !!x)),
                    this.agentService.all$.pipe(filter(x => !!x)),
                ]
            ).pipe(debounce(x => timer(500))).subscribe(() => {
                this.socialSeriesService.clearSocialSearchRequest();
                this.socialSeriesService.doSearch(true);
            }),

            this.socialSeriesService.conflictingSavedFilters$.subscribe(conflictingSavedFilters => {
                this.errorType = SearchErrorTypeEnum.filters;
                this.filterError = conflictingSavedFilters;
            })
        );
    }

    changeTab(tabIndex: number) {
        this.currentTabIndex = tabIndex;
    }

    openFilterDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
            },
            outputs: {}
        };
        this.coreDialogService.open(FilterWidgetComponent, io);
    }

    /**
     * updates the date range and re-runs the search
     *
     * @param dateRange
     *
     * @returns void
     */
    dateRangeChanged(dateRange: DateQueryModel): void {
        this.socialSeriesService.setDateQuery(dateRange);
        this.socialSeriesService.doSearch(true);

        if (this.isArchiveSearch) {
            this.socialArchiveSearchService.setSearchDateRange(this.searchDateRange);
        }
    }

    switchClick(): void {
        if (!this.enableEngagementInsights && this.currentTabIndex === ThemeAnalyticsTabEnum.engagements) {
            this.currentTabIndex = ThemeAnalyticsTabEnum.summary;
        }

        this.socialArchiveSearchService.setenableEngagementInsights(this.enableEngagementInsights);
        this.socialSeriesService.doSearch(true);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}

